import { useRef } from 'react';
import { changeLocale, LOCALE_LABELS, LOCALES, useTranslation, type Locale } from '@most/i18n';
import { useMe, useUpdateLocale } from '@most/auth';
import { cn } from '@most/ui';

/**
 * Locale switcher as a segmented flag-tab strip (TM / RU / EN), flag-only — no text.
 *
 * It lives on the dark header rail, so the strip takes the rail's ink and hover rather than the
 * kit's light-canvas tokens. Flags are tiny inline SVGs — emoji flags degrade to bare "RU"/"TM"
 * letter pairs on Windows — simplified for legibility at ~20px.
 *
 * docs/06 §8: guests persist the choice in localStorage; a logged-in user ALSO saves it to
 * `users.locale` — the column the backend renders their emails and Telegram messages in. So the
 * switch is a real preference change, not merely UI chrome.
 *
 * Semantics: a radiogroup — these buttons set a preference, they do not reveal panels — with a
 * roving tabindex, so the group is one tab-stop and arrow keys move (and select) between flags.
 */
function Flag({ locale }: { locale: Locale }) {
  const shared = 'h-3.5 w-5 shrink-0 rounded-[3px] ring-1 ring-black/20';
  switch (locale) {
    case 'ru':
      return (
        <svg viewBox="0 0 24 16" className={shared} aria-hidden>
          <rect width="24" height="16" fill="#fff" />
          <rect y="5.33" width="24" height="5.34" fill="#0039a6" />
          <rect y="10.67" width="24" height="5.33" fill="#d52b1e" />
        </svg>
      );
    case 'en':
      return (
        <svg viewBox="0 0 24 16" className={shared} aria-hidden>
          <rect width="24" height="16" fill="#012169" />
          <path d="M0 0l24 16M24 0L0 16" stroke="#fff" strokeWidth="3.2" />
          <path d="M0 0l24 16M24 0L0 16" stroke="#c8102e" strokeWidth="1.4" />
          <path d="M12 0v16M0 8h24" stroke="#fff" strokeWidth="5.2" />
          <path d="M12 0v16M0 8h24" stroke="#c8102e" strokeWidth="3" />
        </svg>
      );
    case 'tk':
      return (
        <svg viewBox="0 0 24 16" className={shared} aria-hidden>
          <rect width="24" height="16" fill="#00843d" />
          <rect x="2.5" width="3.5" height="16" fill="#b01e33" />
          <circle cx="11.5" cy="5" r="2.7" fill="#fff" />
          <circle cx="12.5" cy="4.4" r="2.4" fill="#00843d" />
          {/* Stars as dots — real five-pointed stars are sub-pixel noise at this size. */}
          <circle cx="15" cy="2.4" r="0.6" fill="#fff" />
          <circle cx="16.6" cy="3.6" r="0.6" fill="#fff" />
          <circle cx="17" cy="5.6" r="0.6" fill="#fff" />
          <circle cx="15.8" cy="7.3" r="0.6" fill="#fff" />
          <circle cx="13.9" cy="7.6" r="0.6" fill="#fff" />
        </svg>
      );
  }
}

export function LocaleSwitcher() {
  const { t, i18n } = useTranslation('common');
  const { data: me } = useMe();
  const updateLocale = useUpdateLocale();
  const refs = useRef<Array<HTMLButtonElement | null>>([]);

  const current = (i18n.language as Locale) ?? 'ru';

  const onChange = (next: Locale) => {
    if (next === current) return;
    void changeLocale(next);
    if (me) updateLocale.mutate(next);
  };

  const move = (to: number) => {
    const idx = ((to % LOCALES.length) + LOCALES.length) % LOCALES.length;
    refs.current[idx]?.focus();
    onChange(LOCALES[idx]);
  };

  const onKeyDown = (e: React.KeyboardEvent, i: number) => {
    switch (e.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        e.preventDefault();
        move(i + 1);
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        e.preventDefault();
        move(i - 1);
        break;
      case 'Home':
        e.preventDefault();
        move(0);
        break;
      case 'End':
        e.preventDefault();
        move(LOCALES.length - 1);
        break;
    }
  };

  return (
    <div
      role="radiogroup"
      aria-label={t('nav.language')}
      className="inline-flex items-center gap-0.5 rounded-lg bg-rail-hover/40 p-0.5"
    >
      {LOCALES.map((locale, i) => {
        const selected = locale === current;
        return (
          <button
            key={locale}
            ref={(el) => (refs.current[i] = el)}
            type="button"
            role="radio"
            aria-checked={selected}
            aria-label={LOCALE_LABELS[locale]}
            title={LOCALE_LABELS[locale]}
            tabIndex={selected ? 0 : -1}
            onClick={() => onChange(locale)}
            onKeyDown={(e) => onKeyDown(e, i)}
            className={cn(
              'flex h-7 w-8 items-center justify-center rounded-md transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rail-foreground/50',
              selected ? 'bg-rail-hover shadow-sm' : 'opacity-55 hover:bg-rail-hover/60 hover:opacity-100',
            )}
          >
            <Flag locale={locale} />
          </button>
        );
      })}
    </div>
  );
}
